import React, { useState, useEffect, useCallback, useImperativeHandle } from 'react';
import { View, ScrollView } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { Badge } from 'react-native-paper';
import IconButton from '@/components/General/IconButton';
import SheetModal from '@/components/General/SheetModal';
import AdvancedFilterBuilder from '@/components/Filters/AdvancedFilterBuilder';
import QuickFilterManager from '@/components/Filters/QuickFilterManager';
import useAdvancedFilters from '@/utils/hooks/useAdvancedFilters';
import useFilterStore, { useFilter } from '@/data/store/useFilterStore';
import { Query } from '@/utils/react-query/Queries';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { isFreeOrg, FREE_PEOPLE_FILTER_FIELD_IDS } from '@/utils/organizationEntitlements';

/**
 * People Advanced Filter Component
 * Main entry point for filtering people with the new advanced filter system
 * Integrates with the existing filter store for backward compatibility
 * 
 * @param {object} props
 * @param {string} props.filterId - Unique identifier scoping this filter instance in the store
 * @param {object} props.ref - Optional ref exposing `open()` so the guide tour can activate the filter panel
 */
const PeopleAdvancedFilter = ({ filterId, ref, guideNamespace = null }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedQuickFilter, setSelectedQuickFilter] = useState(null);
    useImperativeHandle(ref, () => ({ open: () => setIsOpen(true) }), []);
    // While a tour is hosted inside this sheet it must not count as a guide
    // blocker, and on native the overlay has to render inside this Modal
    // (sibling modals present underneath a pageSheet).
    
    const { setActiveFilter, setQuickFilter, clearFilters: clearFilterStore } = useFilterStore();
    const filterState = useFilter(filterId);
    
    // Plan gate: custom fields, quick filters, roles and small-group filter
    // options are paid-only. Unknown entitlements keep existing behavior.
    const { entitlements } = useOrganizationEntitlements();
    const isFree = isFreeOrg(entitlements);

    // Fetch required data
    const { data: customFields = [] } = Query.personCustomFields.all({ options: { enabled: !isFree } });
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: roles = [] } = Query.roles.all({
        user: loggedInUser,
        options: { enabled: isOpen && !isFree }
    });
    const { data: smallGroupTypes = [] } = Query.smallGroupTypes.all({
        options: { enabled: isOpen && !isFree }
    });

    // Initialize advanced filters hook
    const {
        conditions,
        logic,
        unusedFields,
        groupedFields,
        sectionedFields,
        hasActiveFilters,
        addCondition,
        removeCondition,
        updateCondition,
        updateLogic,
        clearFilters,
        loadFilters,
        getSerializedFilters,
    } = useAdvancedFilters({
        customFields: isFree ? [] : customFields,
        allowedFieldIds: isFree ? FREE_PEOPLE_FILTER_FIELD_IDS : null,
        initialFilters: filterState.peopleFilters ? { ...filterState.peopleFilters, logic: filterState.filterLogic } : null,
    });

    // Dynamic options data for filter inputs
    const dynamicOptionsData = {
        roles: roles,
        smallGroupTypes: smallGroupTypes,
    };

    // Sync with filter store when quick filter is selected
    useEffect(() => {
        if (filterState.quickFilter) {
            setSelectedQuickFilter(filterState.quickFilter);
        }
    }, [filterState.quickFilter]);

    // Handle applying filters
    const handleApply = useCallback(() => {
        if (!isOpen) return;
        const serialized = getSerializedFilters();
    
        if (serialized) {
            setActiveFilter(filterId, {
                peopleFilters: serialized, 
                filterLogic: logic 
            });
        } else {
            clearFilterStore(filterId);
        }
        
        setIsOpen(false);
    }, [isOpen, getSerializedFilters, logic, filterId, setActiveFilter, clearFilterStore]);

    // Handle selecting a quick filter
    const handleSelectQuickFilter = useCallback((quickFilter) => {
        setSelectedQuickFilter(quickFilter);
        
        if (quickFilter?.filterJSON) {
            // Load the quick filter's conditions
            loadFilters({ ...quickFilter.filterJSON, logic: quickFilter.logic });
            
            // Also update the filter store
            setQuickFilter(filterId, {
                quickFilter: { id: quickFilter.id, name: quickFilter.name },
                filterLogic: quickFilter.logic || 'AND',
                peopleFilters: quickFilter.filterJSON,
            });
        }
    }, [loadFilters, filterId, setQuickFilter]);

    // Handle clearing quick filter selection
    const handleClearQuickFilter = useCallback(() => {
        setSelectedQuickFilter(null);
        clearFilters();
        clearFilterStore(filterId);
    }, [clearFilters, filterId, clearFilterStore]);

    // Handle clearing all filters
    const handleClearAll = useCallback(() => {
        setSelectedQuickFilter(null);
        clearFilters();
    }, [clearFilters]);


    return (
        <View>
            {/* Filter Icon Button */}
            <View style={styles.iconButton}>
                <IconButton
                    iconName="filter-outline"
                    iconSize={22}
                    type="textSecondary"
                    tooltipText="Filter people"
                    testID="PeopleFilter"
                    onPress={() => setIsOpen(true)}
                />
                {filterState.peopleFilters && (
                    <View style={styles.badge}><Badge size={12} /></View>
                )}
            </View>

            {/* Filter Modal */}
            <SheetModal
                isOpen={isOpen}
                onClose={handleApply}
                title="Filter People"
                cancelText="Close"
                width={1000}
                guideSurfaceID={guideNamespace ? `${guideNamespace}.filters` : null}
                guideCancelTarget={guideNamespace ? `${guideNamespace}.filter.close` : null}
                // confirmText="Apply"
                onCancel={handleApply}
                // onConfirm={handleApply}
            >
                <ScrollView
                    contentContainerStyle={styles.scrollContent}
                    showsVerticalScrollIndicator={false}
                    keyboardShouldPersistTaps="handled"
                >
                    {/* Quick Filter Manager — quick_filters is a paid module */}
                    {!isFree && <QuickFilterManager
                        guidePrefix={guideNamespace ? `${guideNamespace}.filter` : null}
                        selectedQuickFilter={selectedQuickFilter}
                        onSelectQuickFilter={handleSelectQuickFilter}
                        onClearQuickFilter={handleClearQuickFilter}
                        getSerializedFilters={getSerializedFilters}
                        filterLogic={logic}
                        hasActiveFilters={hasActiveFilters}
                    />}

                    {/* Advanced Filter Builder */}
                    <AdvancedFilterBuilder
                        guidePrefix={guideNamespace ? `${guideNamespace}.filter` : null}
                        conditions={conditions}
                        logic={logic}
                        unusedFields={unusedFields}
                        groupedFields={groupedFields}
                        sectionedFields={sectionedFields}
                        onAddCondition={addCondition}
                        onUpdateCondition={updateCondition}
                        onRemoveCondition={removeCondition}
                        onUpdateLogic={updateLogic}
                        onClearFilters={handleClearAll}
                        dynamicOptionsData={dynamicOptionsData}
                        showHeader={true}
                    />
                </ScrollView>
            </SheetModal>
        </View>
    );
};

const styles = StyleSheet.create((_theme) => ({
    iconButton: {
        position: 'relative',
    },
    badge: {
        position: 'absolute',
        top: 1,
        right: -2,
    },
    scrollContent: {
        paddingBottom: 100,
    },
}));

export default PeopleAdvancedFilter;
